import { useState, useMemo, useEffect, type ComponentType } from 'react';
import axios from 'axios';
import signUp from '@/assets/imgs/signUp.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '@/lib/i18n/useLocale';
import { LOGIN_URLS } from '../config/loginUrls';
import { projectTypeFromProductQuery } from '../config/productFromUrl';
import { getRegisterErrorMessage, isRegisterCompletedDespiteBackendError, postRegister } from '../utils/registerApi';
import { persistHrmAuthFromRegisterResponse } from '../utils/hrmSessionAuth';
import { apiGet } from '@/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import type { PricingPlan } from '@/features/pricing/data/configs.data';
// import bgimg from '@/assets/imgs/bg.jpg';
import SignUpForm from '../components/SignUpForm';
import CustomerFeedback from '@/assets/imgs/CustomerFeedback.png';
// import Employee from '@/assets/imgs/Employeee.png';
// import rent from '@/assets/imgs/rent.jpg';
import Clinico from '@/assets/imgs/Clinico.png';
import hrMangement from '@/assets/svg/three.svg';
import ElectricCar from './ElectricCar';
import Employe from '@/assets/svg/products/Crm1';
import authBg from '@/assets/authBg.png';

const SIGNUP_SIDE_IMAGE_BY_PRODUCT: Record<string, string> = {
    // crm: Employe,
    gym: CustomerFeedback,
    gyms: CustomerFeedback,
    education: CustomerFeedback,
    clinics: CustomerFeedback,
    'educational-centers': CustomerFeedback,
    'hr-management': hrMangement,
    clinico: Clinico,
};

const SIGNUP_SIDE_COMPONENT_BY_PRODUCT: Record<string, ComponentType> = {
    fleet: ElectricCar,
    crm: Employe,
};

const SignUp = () => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const plan_id = searchParams.get('plan_id');
    const social = searchParams.get('social');
    const productQuery = searchParams.get('product');
    const presetProjectType = useMemo(() => projectTypeFromProductQuery(productQuery), [productQuery]);

    const productKey = useMemo(() => (productQuery ?? '').trim().toLowerCase(), [productQuery]);

    const signUpSideImageSrc = useMemo(() => {
        if (!productKey) return signUp;
        return SIGNUP_SIDE_IMAGE_BY_PRODUCT[productKey] ?? signUp;
    }, [productKey]);

    const SignUpSideComponent = useMemo(
        () => (productKey ? SIGNUP_SIDE_COMPONENT_BY_PRODUCT[productKey] : undefined),
        [productKey],
    );

    const form = useForm({
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            password: '',
            project_type: '',
            company_logo: null as File | null,
        },
    });

    useEffect(() => {
        form.setValue('project_type', presetProjectType ?? '');
    }, [presetProjectType, form]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [planNameFromApi, setPlanNameFromApi] = useState<string | null>(null);
    /** Only send plan_id when it exists in website-subscriptions — avoids standalone API null->id crash after user is created. */
    const [verifiedPlanId, setVerifiedPlanId] = useState<string | null>(null);

    useEffect(() => {
        if (!plan_id) {
            setPlanNameFromApi(null);
            setVerifiedPlanId(null);
            return;
        }
        let cancelled = false;
        const loadPlan = async () => {
            try {
                const response = await apiGet({
                    endpoint: endpoints.subscriptions.list,
                });
                const payload = response.data as { data?: PricingPlan[] } | PricingPlan[] | undefined;
                const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
                const found = rows.find((p) => String(p.id) === plan_id);
                if (!cancelled) {
                    setPlanNameFromApi(found?.name ?? null);
                    setVerifiedPlanId(found ? String(found.id) : null);
                }
            } catch {
                if (!cancelled) {
                    setPlanNameFromApi(null);
                    setVerifiedPlanId(null);
                }
            }
        };
        loadPlan();
        return () => {
            cancelled = true;
        };
    }, [plan_id]);

    const completeRegistration = (projectType: string) => {
        setSuccess(t('signUp.success'));
        toast.success(t('signUp.success'), {
            position: 'top-right',
            duration: 3000,
        });
        form.reset({
            name: '',
            email: '',
            phone: '',
            password: '',
            project_type: presetProjectType ?? '',
            company_logo: null,
        });
        setTimeout(() => {
            if (projectType === 'crm') {
                const qs = new URLSearchParams();
                if (verifiedPlanId) qs.set('plan_id', verifiedPlanId);
                if (social) qs.set('social', social);
                if (productQuery) qs.set('product', productQuery);
                const search = qs.toString();
                navigate(localizedPath('/checkout', search ? { search } : undefined));
                return;
            }
            if (projectType === 'hr_management') {
                navigate(localizedPath('/branch-create'));
                return;
            }
            const loginUrl = LOGIN_URLS[projectType] || LOGIN_URLS.crm;
            window.location.href = loginUrl;
        }, 1500);
    };

    const onSubmit = async (data: {
        name: string;
        email: string;
        phone: string;
        password: string;
        project_type: string;
        company_logo: File | null;
    }) => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const res = await postRegister(data.project_type, data, { social, verifiedPlanId });
            if (data.project_type === 'hr_management') {
                persistHrmAuthFromRegisterResponse(res.data);
            }
            completeRegistration(data.project_type);
        } catch (err: unknown) {
            if (isRegisterCompletedDespiteBackendError(err)) {
                if (data.project_type === 'hr_management' && axios.isAxiosError(err)) {
                    persistHrmAuthFromRegisterResponse(err.response?.data);
                }
                completeRegistration(data.project_type);
                return;
            }
            setError(
                getRegisterErrorMessage(err, {
                    fallback: t('signUp.error'),
                    invalidApi: t('signUp.errorInvalidApiResponse'),
                    emailAlreadyRegistered: t('signUp.emailAlreadyRegistered'),
                }),
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            // style={{ backgroundImage: `url(${bgimg})` }}
            className="flex min-h-[100dvh] w-full flex-col bg-[#E6E6E6] dark:bg-card bg-cover bg-center bg-no-repeat md:flex-row md:items-stretch lg:gap-8 gap-4 justify-between lg:p-8 p-6"
        >
            <div className="my-auto flex w-full shrink-0 bg-tertiary-blue-50 dark:bg-[#191919] shadow-2xl rounded-[24px] md:my-0 md:min-h-0 md:flex-1 md:self-stretch">
                <div className="flex w-full flex-col items-center py-6 lg:px-[60px] md:px-[30px] px-4 md:justify-center">
                    <h3 className="text-primery-blue-700 dark:text-[#437DB3] lg:text-[40px] md:text-[30px] text-[20px]">
                        {t('signUp.welcome')}
                    </h3>
                    <p className="text-card-foreground lg:text-[24px] md:text-[20px] text-[18px] mt-2">
                        {t('signUp.subtitle')}
                    </p>

                    <SignUpForm
                        form={form}
                        productQuery={productQuery}
                        planNameFromApi={planNameFromApi}
                        presetProjectType={presetProjectType ?? ''}
                        onSubmit={onSubmit}
                        loading={loading}
                        error={error}
                        success={success}
                    />
                </div>
            </div>

            {/* Side visual: image or custom component by ?product= */}
            <div className="hidden w-full shrink-0 md:flex md:min-h-0 md:flex-1 md:items-center md:justify-center">
                {SignUpSideComponent ? (
                    <div className="flex w-full max-h-[min(550px,60vh)] items-center justify-center rounded-[24px]">
                        <SignUpSideComponent />
                    </div>
                ) : (
                    <img
                        key={signUpSideImageSrc}
                        src={authBg}
                        alt=""
                        className="max-h-[760px] h-full w-full rounded-[24px] object-cover"
                    />
                )}
            </div>
        </div>
    );
};

export default SignUp;
